// © Microsoft Corporation. All rights reserved.

import { ReactionBreakdownTelemetryObjects, reactionBreakdownTelemetryActionDispatchers, reactionBreakdownTelemetryContracts } from "./ReactionBreakdown.telemetry";
import { enums, hasThemeBase, renderEmojiByType, TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid } from "@msnews/social-utils";

import React from "react";
import ReactionBreakdownListItem from "./ReactionBreakdownListItem";
import { SocialProps } from "../Social.props";
import { String } from "typescript-string-operations";
import { capitalize } from "lodash-es";
import { classNames } from "@microsoft/fast-web-utilities";

interface ReactionBreakdownState {
    activeTab: string;
}

export default class ReactionBreakdown extends React.Component<SocialProps, ReactionBreakdownState> {
    telemetryObjects: ReactionBreakdownTelemetryObjects;
    containerRef: HTMLDivElement;

    constructor(props) {
        super(props);

        this.state = {
            activeTab: props.activeTab || "all"
        };
        this.containerRef = null;
    }

    componentDidMount() {
        if (this.containerRef) {
            dispatchTelemetryEvent(TelemetryEventMap.genericLoaded, this.containerRef);
        }
        this.props.reactionBreakdownConnector.onReactionBreakdownComponentConnected({ commentId: this.props.commentId, contentId: this.props.contentId });
    }

    componentDidUpdate(prevProps: SocialProps) {
        if (this.props.id != null && this.props.market != null && prevProps.id != this.props.id || prevProps.market != this.props.market) {
            this.props.reactionBreakdownConnector.onReactionBreakdownComponentConnected({ commentId: this.props.commentId, contentId: this.props.contentId });
        }

        if (!prevProps.reactionBreakdown?.reactions && this.props.reactionBreakdown?.reactions) {
            this.props.markTtvr && this.props.markTtvr();
        }
    }

    componentWillUnmount() {
        this.props.reactionBreakdownConnector.onReactionBreakdownComponentDisconnected();
    }

    showTab(event, tabName) {
        this.sendTabClickedTelemetry(event.currentTarget, tabName);

        this.setState({
            activeTab: tabName
        });
    }

    prepareActiveTabItems(args) {
        if (!args.items || !args.activeTab || !args.users) { console.warn("ReactionBreakdown missing argument!"); }
        const meId = args.me.id;

        const attachUserDataToItem = (item, userId) => {
            const userState = args.users[userId];
            return {...item, user: userState.data};
        };

        if (!args.breakdownWithTabs) {
            // self id is `meId` if the user is logged in, `undefined` if the user is anonymous
            const itemOfSelf = args.items.all.find(item => item.userId === meId || item.userId === undefined);
            const selfId = itemOfSelf && itemOfSelf.userId;
            return itemOfSelf ? [attachUserDataToItem(itemOfSelf, selfId)] : [];
        }

        return args.items[args.activeTab]
            .sort((a, b) => {
                if (a.userId == meId) {
                    return -1;
                } else if (b.userId == meId) {
                    return 1;
                } else {
                    return 0;
                }
            })
            .map(item => attachUserDataToItem(item, item.userId));
    }

    sendTabClickedTelemetry(target, tabName) {
        reactionBreakdownTelemetryActionDispatchers.tabClicked(target, this.telemetryObjects && this.telemetryObjects.tabs && this.telemetryObjects.tabs[tabName]);
    }

    getOtherReactionsString(reaction: string, otherCount: number) {
        if (otherCount < 1) {
            console.warn(`There is no reaction string for this reaction count: ${otherCount}`);
            return "";
        }
        const t = this.props.t;
        // {reaction: [<single reaction string>, <multiple reactions string>]}
        const reactionStrings = {
            [enums.reactions.love]: [t.ReactionBreakDownOneOtherLoves, t.ReactionBreakDownSomeOthersLove],
            [enums.reactions.like]: [t.ReactionBreakDownOneOtherLikes, t.ReactionBreakDownSomeOthersLike],
            [enums.reactions.surprised]: [t.ReactionBreakDownOneOtherIsSurprised, t.ReactionBreakDownSomeOthersAreSurprised],
            [enums.reactions.angry]: [t.ReactionBreakDownOneOtherIsAngry, t.ReactionBreakDownSomeOthersAreAngry],
            [enums.reactions.sad]: [t.ReactionBreakDownOneOtherIsSad, t.ReactionBreakDownSomeOthersAreSad],
            [enums.reactions.thinking]: [t.ReactionBreakDownOneOtherIsThinking, t.ReactionBreakDownSomeOthersAreThinking]
        };
        return otherCount === 1 ? reactionStrings[reaction][0] : String.Format(reactionStrings[reaction][1], {otherCount: otherCount});
    }

    render() {
        const { componentConnector, reactionBreakdown, me, users, managedClasses, usersConnector, market, responsiveClass, t, mainTelemetryObject, theme, contentId, contents, commentId} = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        const isContentModerated = contentId && contents && contents[contentId] && contents[contentId].commentStatus === "On";
        const breakdownWithTabs: boolean = !!commentId || isContentModerated;
        const reactions = reactionBreakdown && reactionBreakdown.reactions;
        if (!reactions || !me) { return null; }
        const {items, reactionCounts} = reactions;
        const {activeTab} = this.state;
        const activeTabItems = this.prepareActiveTabItems({items, users, activeTab, me, breakdownWithTabs});
        // Reaction Counts
        const moreCounts: {[key: string]: number} = {};
        reactionCounts.all = 0;
        if (reactionCounts) {
            reactionCounts.all = Object.values(reactionCounts).reduce(($sum, $count) => $sum + $count, 0);

            Object.keys(reactionCounts).filter(key => key !== "all").forEach(key => {
                if (breakdownWithTabs) {
                    moreCounts[key] = reactionCounts[key] - items[key].length;
                } else {
                    const selfReacted = activeTabItems.length === 1;
                    const reactionType = selfReacted && activeTabItems[0].rateValue;
                    moreCounts[key] = (reactionType === key) ? reactionCounts[key]-1 : reactionCounts[key];
                }
            });
        }

        /** Generate Telemetry Objects */
        const cmsId = (contentId && contents && contents[contentId] && contents[contentId].cmsId);
        this.telemetryObjects = {
            container: addOrUpdateTelemetryObject(
                mainTelemetryObject,
                this.props.commentId ? reactionBreakdownTelemetryContracts.commentReactionBreakdown : reactionBreakdownTelemetryContracts.contentReactionBreakdown,
                { id: commentId || cmsId},
                this.telemetryObjects && this.telemetryObjects.container
            ),
            tabs: {}
        };
        Object.keys(reactionCounts).map(key => {
            if (this.telemetryObjects && this.telemetryObjects.container) {
                this.telemetryObjects.tabs[key] = addOrUpdateTelemetryObject(
                    this.telemetryObjects.container,
                    {...reactionBreakdownTelemetryContracts.tab},
                    { id: commentId || cmsId, headline: capitalize(key) },
                    this.telemetryObjects && this.telemetryObjects.tabs && this.telemetryObjects.tabs[key]
                );
            }
        });

        const OtherReactionsItem = ({reaction}) => {
            if (!moreCounts[reaction]) { return null; }
            return (
                <li className="cwt-rest-reactions">
                    <div className="float-item">
                        <i className="cwt-reaction-more cwt-icon">
                            {renderEmojiByType(reaction)}
                        </i>
                        <div className="cwt-name">{this.getOtherReactionsString(reaction, moreCounts[reaction])}</div>
                    </div>
                </li>
            );
        };

        return (
            <div
                className={classNames(
                    managedClasses.reactionBreakdownContainer,
                    responsiveClass,
                    [managedClasses.reactionBreakdownSuperAppContainer, isSuperApp]
                )}
                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.container, true)}
            >
                <div className="cwt-reaction-breakdown-inner">
                    <header className="cwt-reaction-breakdown-header">
                        <h1 className="cwt-title">{t.ReactionBreakDownTitle}</h1>
                        <ul className="cwt-tabs" role="tablist">
                            <li className={`${activeTab == "all" && "active"} cwt-tab-item cwt-link`} role="tab" aria-label={t.AriaLabelsAllReactions} onClick={e => this.showTab(e, "all")}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.tabs && this.telemetryObjects.tabs["all"], true)}>
                                <i className="cwt-icon">
                                    <div className="cwt-badge">{t.ReactionBreakDownTabText}</div>
                                    <span className="cwt-icon-label">{reactionCounts.all}</span>
                                </i>
                            </li>

                            {
                                breakdownWithTabs && Object.keys(reactionCounts).filter(key => key != "all").map(key =>
                                    reactionCounts[key] > 0 &&
                                        <li className={`${activeTab == key && "active"} cwt-tab-item cwt-link`} aria-selected={activeTab == key}
                                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.tabs && this.telemetryObjects.tabs[key], true)}
                                            role="tab" aria-label={key} onClick={e => this.showTab(e, key)} key={key}>
                                            <i className="cwt-icon">
                                                {renderEmojiByType(key)}
                                                <span className="cwt-icon-label">{reactionCounts[key]}</span>
                                            </i>
                                        </li>
                                )
                            }
                        </ul>
                        <hr className="cwt-h-seperator"/>
                    </header>

                    <div className="cwt-tab-content">
                        <ul className="cwt-reacting-users">
                            {activeTabItems.map((item, index) => {
                                const isAnonymous = item.user.state == enums.userStateType.anonymous;
                                return <ReactionBreakdownListItem
                                    key={index}
                                    {...{isAnonymous, managedClasses, usersConnector, market, ...item, t, theme, componentConnector, mainTelemetryObject: this.telemetryObjects && this.telemetryObjects.container}}
                                />;
                            })}
                            {activeTab === "all" ?
                                Object.entries(moreCounts).sort((t0, t1) => t1[1]-t0[1]).map(tab => <OtherReactionsItem key={tab[0]} reaction={tab[0]} />):
                                <OtherReactionsItem reaction={activeTab} />
                            }
                        </ul>
                    </div>
                </div>
            </div>
        );
    }
}
